import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BellOutlined,
  ReloadOutlined,
  SaveOutlined,
  CheckCircleOutlined,
  MailOutlined,
  MessageOutlined,
  AppstoreOutlined,
  SettingOutlined,
  UndoOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import MainLayout from '../../layouts/MainLayout';
import { useAuth } from '../../contexts/AuthContext';
import { actionApi } from '../../utils/actionApi';
import {
  message,
  Spin,
  Button,
  Card,
  Row,
  Col,
  Switch,
  Table,
  Space,
  Typography,
  Divider,
  Tag,
} from 'antd';

const { Title, Text } = Typography;

const NotificationSettings = () => {
  const navigate = useNavigate();
  const { actionToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState([]);
  const [originalPreferences, setOriginalPreferences] = useState([]);

  // انواع اعلان‌ها
  const notificationTypes = [
    { value: 'action_assigned', label: 'تخصیص اقدام', description: 'زمانی که یک اقدام به شما تخصیص داده می‌شود' },
    { value: 'explanation_requested', label: 'درخواست توضیح', description: 'زمانی که از شما درخواست توضیح می‌شود' },
    { value: 'deadline_approaching', label: 'نزدیک شدن به مهلت', description: 'زمانی که مهلت یک اقدام نزدیک می‌شود' },
    { value: 'action_overdue', label: 'اقدام معوق', description: 'زمانی که مهلت یک اقدام گذشته است' },
    { value: 'file_uploaded', label: 'آپلود فایل', description: 'زمانی که فایلی به اقدام اضافه می‌شود' },
    { value: 'status_changed', label: 'تغییر وضعیت', description: 'زمانی که وضعیت یک اقدام تغییر می‌کند' },
    { value: 'action_closed', label: 'بسته شدن اقدام', description: 'زمانی که یک اقدام بسته می‌شود' },
    { value: 'action_item_added', label: 'افزودن آیتم اقدام', description: 'زمانی که آیتمی به اقدام اضافه می‌شود' },
    { value: 'comment_added', label: 'افزودن نظر', description: 'زمانی که نظری به اقدام اضافه می‌شود' },
    { value: 'comment_mentioned', label: 'ذکر در نظر', description: 'زمانی که در یک نظر از شما نام برده می‌شود' },
    { value: 'request_created', label: 'ایجاد درخواست', description: 'زمانی که درخواستی ایجاد می‌شود' },
    { value: 'request_approved', label: 'تأیید درخواست', description: 'زمانی که درخواستی تأیید می‌شود' },
    { value: 'request_rejected', label: 'رد درخواست', description: 'زمانی که درخواستی رد می‌شود' },
  ];

  // دریافت برچسب نوع اعلان
  const getNotificationTypeLabel = (type) => {
    const found = notificationTypes.find((t) => t.value === type);
    return found ? found.label : type;
  };

  // دریافت توضیحات نوع اعلان
  const getNotificationTypeDescription = (type) => {
    const found = notificationTypes.find((t) => t.value === type);
    return found ? found.description : '';
  };

  // دریافت تنظیمات
  const fetchPreferences = async () => {
    if (!actionToken) return;

    setLoading(true);
    try {
      const res = await actionApi.getNotificationPreferences();
      // پاسخ یک array است که شامل تمام انواع اعلان می‌شود (حتی اگر تنظیماتی نداشته باشند)
      const prefs = res?.data || [];
      
      // استفاده از label از API به جای label محلی
      // اگر label در API وجود نداشت، از label محلی استفاده می‌کنیم
      const allPrefs = prefs.map((pref) => ({
        ...pref,
        label: pref.label || getNotificationTypeLabel(pref.type),
      }));

      setPreferences(allPrefs);
      setOriginalPreferences(JSON.parse(JSON.stringify(allPrefs)));
    } catch (error) {
      console.error('Load preferences error:', error);
      message.error('خطا در دریافت تنظیمات اعلان');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (actionToken) {
      fetchPreferences();
    }
  }, [actionToken]);

  // به‌روزرسانی تنظیمات
  const updatePreference = (type, field, value) => {
    setPreferences((prev) =>
      prev.map((pref) =>
        pref.type === type ? { ...pref, [field]: value } : pref
      )
    );
  };

  // ذخیره تنظیمات
  const handleSave = async () => {
    if (!actionToken) return;

    setSaving(true);
    try {
      // پیدا کردن فقط تنظیمات تغییر یافته
      const changedPreferences = {};
      
      preferences.forEach((pref) => {
        const originalPref = originalPreferences.find((op) => op.type === pref.type);
        
        // بررسی اینکه آیا این تنظیمات تغییر کرده است
        if (!originalPref) {
          // اگر تنظیمات جدیدی است، آن را اضافه می‌کنیم
          changedPreferences[pref.type] = {
            via_in_app: Boolean(pref.via_in_app),
            via_sms: Boolean(pref.via_sms),
            via_email: Boolean(pref.via_email),
            enabled: Boolean(pref.enabled),
          };
        } else {
          // مقایسه با تنظیمات اصلی
          const hasChanged = 
            originalPref.via_in_app !== pref.via_in_app ||
            originalPref.via_sms !== pref.via_sms ||
            originalPref.via_email !== pref.via_email ||
            originalPref.enabled !== pref.enabled;
          
          if (hasChanged) {
            // فقط تنظیمات تغییر یافته را اضافه می‌کنیم
            changedPreferences[pref.type] = {
              via_in_app: Boolean(pref.via_in_app),
              via_sms: Boolean(pref.via_sms),
              via_email: Boolean(pref.via_email),
              enabled: Boolean(pref.enabled),
            };
          }
        }
      });

      // اگر هیچ تغییری نبود، پیام نمایش می‌دهیم
      if (Object.keys(changedPreferences).length === 0) {
        message.info('هیچ تغییری برای ذخیره وجود ندارد');
        setSaving(false);
        return;
      }

      const res = await actionApi.updateNotificationPreferences({ preferences: changedPreferences });
      if (res?.success) {
        message.success('تنظیمات با موفقیت ذخیره شد');
        // به‌روزرسانی تنظیمات با پاسخ API (که شامل label و is_customized است)
        const updatedPrefs = res?.data || preferences;
        const allPrefs = updatedPrefs.map((pref) => ({
          ...pref,
          label: pref.label || getNotificationTypeLabel(pref.type),
        }));
        setPreferences(allPrefs);
        setOriginalPreferences(JSON.parse(JSON.stringify(allPrefs)));
      }
    } catch (error) {
      console.error('Save preferences error:', error);
      message.error('خطا در ذخیره تنظیمات');
    } finally {
      setSaving(false);
    }
  };

  // بررسی تغییرات
  const hasChanges = () => {
    return JSON.stringify(preferences) !== JSON.stringify(originalPreferences);
  };

  // بازنشانی تغییرات
  const handleReset = () => {
    setPreferences(JSON.parse(JSON.stringify(originalPreferences)));
    message.info('تغییرات بازنشانی شدند');
  };

  // بازنشانی به حالت اولیه (از API)
  const handleResetToDefault = async () => {
    if (!actionToken) return;

    setSaving(true);
    try {
      const res = await actionApi.resetNotificationPreferences();
      if (res?.success) {
        message.success('تنظیمات به حالت اولیه بازگردانده شد');
        // دریافت مجدد تنظیمات از API
        await fetchPreferences();
      } else {
        message.error(res?.message || 'خطا در بازنشانی تنظیمات');
      }
    } catch (error) {
      console.error('Reset preferences error:', error);
      message.error('خطا در بازنشانی تنظیمات');
    } finally {
      setSaving(false);
    }
  };

  // دریافت تنظیمات یک نوع اعلان
  const getPreference = (type) => {
    const pref = preferences.find((p) => p.type === type);
    if (pref) {
      return pref;
    }
    // اگر تنظیماتی از API نیامده، از مقادیر پیش‌فرض استفاده می‌کنیم
    return {
      type,
      label: getNotificationTypeLabel(type),
      via_in_app: true,
      via_sms: false,
      via_email: false,
      enabled: true,
      is_customized: false,
    };
  };

  const columns = [
    {
      title: 'نوع اعلان',
      dataIndex: 'type',
      key: 'type',
      width: 200,
      render: (type, record) => (
        <div>
          <div className="flex items-center gap-2">
            <div
              className="font-medium text-gray-900 dark:text-white"
              style={{ fontFamily: 'CustomFont' }}
            >
              {record.label || getNotificationTypeLabel(type)}
            </div>
            {record.is_customized && (
              <Tag color="blue" size="small" style={{ fontFamily: 'CustomFont', margin: 0 }}>
                شخصی‌سازی شده
              </Tag>
            )}
          </div>
          <div
            className="text-xs text-gray-500 dark:text-gray-400 mt-1"
            style={{ fontFamily: 'CustomFont' }}
          >
            {getNotificationTypeDescription(type)}
          </div>
        </div>
      ),
    },
    {
      title: 'فعال/غیرفعال',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 120,
      align: 'center',
      render: (enabled, record) => (
        <Switch
          checked={enabled}
          checkedChildren="فعال"
          unCheckedChildren="غیرفعال"
          onChange={(checked) => updatePreference(record.type, 'enabled', checked)}
        />
      ),
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <AppstoreOutlined />
          <span style={{ fontFamily: 'CustomFont' }}>در برنامه</span>
        </div>
      ),
      dataIndex: 'via_in_app',
      key: 'via_in_app',
      width: 120,
      align: 'center',
      render: (via_in_app, record) => (
        <Switch
          checked={via_in_app && record.enabled}
          disabled={!record.enabled}
          checkedChildren="بله"
          unCheckedChildren="خیر"
          onChange={(checked) => updatePreference(record.type, 'via_in_app', checked)}
        />
      ),
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <MessageOutlined />
          <span style={{ fontFamily: 'CustomFont' }}>پیامک</span>
        </div>
      ),
      dataIndex: 'via_sms',
      key: 'via_sms',
      width: 120,
      align: 'center',
      render: (via_sms, record) => (
        <Switch
          checked={via_sms && record.enabled}
          disabled={!record.enabled}
          checkedChildren="بله"
          unCheckedChildren="خیر"
          onChange={(checked) => updatePreference(record.type, 'via_sms', checked)}
        />
      ),
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <MailOutlined />
          <span style={{ fontFamily: 'CustomFont' }}>ایمیل</span>
        </div>
      ),
      dataIndex: 'via_email',
      key: 'via_email',
      width: 120,
      align: 'center',
      render: (via_email, record) => (
        <Switch
          checked={via_email && record.enabled}
          disabled={!record.enabled}
          checkedChildren="بله"
          unCheckedChildren="خیر"
          onChange={(checked) => updatePreference(record.type, 'via_email', checked)}
        />
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="px-4 md:px-6 py-5 w-full">
        {/* هدر */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <Button
                icon={<ArrowRightOutlined />}
                onClick={() => navigate('/action/notifications')}
                style={{ fontFamily: 'CustomFont' }}
              >
                بازگشت
              </Button>
              <div className="relative">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-purple-400 via-indigo-400 to-purple-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
                  <SettingOutlined className="text-white text-xl" />
                </div>
              </div>
              <div>
                <Title
                  level={2}
                  className="!mb-0 text-gray-900 dark:text-white"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  تنظیمات اعلان
                </Title>
                {/* <Text
                  className="text-sm text-gray-500 dark:text-gray-400"
                  style={{ fontFamily: 'CustomFont' }}
                >
                  مدیریت نحوه دریافت اعلان‌های سیستم
                </Text> */}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              {/* <Button
                onClick={fetchPreferences}
                icon={<ReloadOutlined />}
                className="flex items-center justify-center"
                disabled={saving}
              >
                به‌روزرسانی
              </Button> */}
              <Button
                onClick={handleResetToDefault}
                icon={<UndoOutlined />}
                danger
                loading={saving}
                disabled={saving}
                style={{ fontFamily: 'CustomFont' }}
              >
                بازنشانی به حالت اولیه
              </Button>
              {hasChanges() && (
                <Button onClick={handleReset} disabled={saving}>
                  بازنشانی تغییرات
                </Button>
              )}
              <Button
                onClick={handleSave}
                icon={<SaveOutlined />}
                type="primary"
                loading={saving}
                disabled={!hasChanges() || saving}
                className="bg-emerald-500 hover:bg-emerald-600 border-emerald-500 hover:border-emerald-600"
                style={{ fontFamily: 'CustomFont' }}
              >
                ذخیره تغییرات
              </Button>
            </div>
          </div>
        </div>

        {/* توضیحات */}
        <Card className="mb-6 bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <BellOutlined className="text-blue-500 text-xl mt-1" />
            <div>
              <Text
                className="text-sm font-medium text-blue-900 dark:text-blue-100 block mb-1"
                style={{ fontFamily: 'CustomFont' }}
              >
                راهنمای تنظیمات اعلان
              </Text>
              <Text
                className="text-sm text-blue-700 dark:text-blue-300"
                style={{ fontFamily: 'CustomFont' }}
              >
                می‌توانید برای هر نوع اعلان، نحوه دریافت آن را مشخص کنید. اگر یک نوع اعلان را غیرفعال کنید،
                هیچ اعلانی از آن نوع دریافت نخواهید کرد. همچنین می‌توانید مشخص کنید که اعلان‌ها از طریق
                برنامه، پیامک یا ایمیل دریافت شوند.
              </Text>
            </div>
          </div>
        </Card>

        {/* جدول تنظیمات */}
        <Card>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Spin size="large" />
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={preferences.length > 0 
                ? preferences.map((pref) => ({
                    key: pref.type,
                    ...pref,
                  }))
                : notificationTypes.map((type) => ({
                    key: type.value,
                    ...getPreference(type.value),
                  }))
              }
              pagination={false}
              rowClassName={(record) =>
                !record.enabled ? 'opacity-50' : ''
              }
            />
          )}
        </Card>

        {/* راهنمای ستون‌ها */}
        <Card className="mt-6">
          <Title level={4} style={{ fontFamily: 'CustomFont' }}>
            راهنمای ستون‌ها
          </Title>
          <Divider />
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircleOutlined className="text-emerald-500" />
                <Text strong style={{ fontFamily: 'CustomFont' }}>
                  فعال/غیرفعال
                </Text>
              </div>
              <Text
                className="text-sm text-gray-600 dark:text-gray-400"
                style={{ fontFamily: 'CustomFont' }}
              >
                با غیرفعال کردن، هیچ اعلانی از این نوع دریافت نخواهید کرد
              </Text>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div className="flex items-center gap-2 mb-2">
                <AppstoreOutlined className="text-blue-500" />
                <Text strong style={{ fontFamily: 'CustomFont' }}>
                  در برنامه
                </Text>
              </div>
              <Text
                className="text-sm text-gray-600 dark:text-gray-400"
                style={{ fontFamily: 'CustomFont' }}
              >
                دریافت اعلان در داخل برنامه
              </Text>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div className="flex items-center gap-2 mb-2">
                <MessageOutlined className="text-orange-500" />
                <Text strong style={{ fontFamily: 'CustomFont' }}>
                  پیامک
                </Text>
              </div>
              <Text
                className="text-sm text-gray-600 dark:text-gray-400"
                style={{ fontFamily: 'CustomFont' }}
              >
                دریافت اعلان از طریق پیامک
              </Text>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div className="flex items-center gap-2 mb-2">
                <MailOutlined className="text-purple-500" />
                <Text strong style={{ fontFamily: 'CustomFont' }}>
                  ایمیل
                </Text>
              </div>
              <Text
                className="text-sm text-gray-600 dark:text-gray-400"
                style={{ fontFamily: 'CustomFont' }}
              >
                دریافت اعلان از طریق ایمیل
              </Text>
            </Col>
          </Row>
        </Card>
      </div>
    </MainLayout>
  );
};

export default NotificationSettings;

